import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon, UMSidePanel } from '../../urbanmap/_components';
import { PlanCreateForm } from './PlanCreateForm';
import { PlanMetrics } from './PlanMetrics';
import { PlanStepsEditor } from './PlanStepsEditor';
import { progressLabel } from './plans.data';
import { useCreatePlan, useDevelopmentPlans } from './plans.hooks';
import { UMR } from './tokens';
import './development-plans.css';

interface Props {
  projectId: string;
  onClose: () => void;
  flash: (message: string) => void;
  /** Plan ouvert, conservé par la vue projet pour rester affiché sur la carte quand on change de panneau. */
  selected: string | null;
  onSelect: (planId: string | null) => void;
}

/** Variante A retenue : liste des plans, puis plan choisi avec ses trois étapes. */
export function DevelopmentPlansPanel({ projectId, onClose, flash, selected, onSelect: setSelected }: Props) {
  const plans = useDevelopmentPlans(projectId);
  const create = useCreatePlan(projectId);
  const [creating, setCreating] = useState(false);
  const plan = selected ? plans.data?.find((p) => p.id === selected) ?? null : null;

  const onCreate = (name: string) => create.mutate({ name }, {
    onSuccess: (p) => { setCreating(false); setSelected(p.id); flash(`${p.name} créé · commencez par choisir les bâtiments`); },
  });

  let body;
  if (plan) {
    body = <PlanStepsEditor key={plan.id} projectId={projectId} plan={plan} onBack={() => setSelected(null)} flash={flash} />;
  } else if (creating) {
    body = <PlanCreateForm plans={plans.data ?? []} pending={create.isPending} serverError={create.error?.message ?? null}
      onSubmit={onCreate} onCancel={() => { create.reset(); setCreating(false); }} />;
  } else {
    body = (
      <div className="pnv2-dp-list">
        <NJButton label="Nouveau plan de développement" icon="add" scale="sm" onClick={() => setCreating(true)} />
        {plans.isLoading && <NJText scale="sm">Chargement des plans…</NJText>}
        {plans.isError && <NJText scale="sm" className="pnv2-dp-error">Impossible de charger les plans. Réessayez.</NJText>}
        {plans.data && plans.data.length === 0 && (
          <div className="pnv2-dp-empty">
            <UMIcon name="route" size={28} color={UMR.textSecondary} />
            <NJText as="strong" scale="sm">Aucun plan de développement</NJText>
            <NJText scale="sm">Créez un plan pour choisir des bâtiments, tracer le réseau puis phaser les raccordements.</NJText>
          </div>
        )}
        {plans.data && plans.data.length > 0 && (
          <>
            <NJText scale="xs" className="pnv2-dp-count">
              {plans.data.length} plan{plans.data.length > 1 ? 's' : ''} · dernière modification en premier
            </NJText>
            <ul className="pnv2-dp-items" aria-label="Plans de développement du projet">
              {plans.data.map((p) => (
                <li key={p.id}>
                  <button type="button" className="pnv2-dp-item" onClick={() => setSelected(p.id)}>
                    <span className="pnv2-dp-item-text">
                      <NJText as="strong" scale="sm">{p.name}</NJText>
                      <NJText as="span" scale="xs">{progressLabel(p)}</NJText>
                      <PlanMetrics plan={p} />
                      <NJText as="span" scale="xs" className="pnv2-dp-muted">Modifié le {p.updatedAt}</NJText>
                    </span>
                    <UMIcon name="chevron_right" size={20} color={UMR.textSecondary} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <div data-comment-view="panel-plans" style={{ display: 'contents' }}>
      <UMSidePanel side="left" width={360} title="Plans de développement" onClose={onClose}>{body}</UMSidePanel>
    </div>
  );
}
